import envs from 'envs';

import { secondsToDaysHoursMinutes } from './converter';
import datter from './datter';

export const isValidDate = date => date instanceof Date && !Number.isNaN(date.getTime());

export const isYyyyMmDd = value => /^\d{4}-\d{2}-\d{2}$/.test(value);

export const parseLocalCalendarDate = value => {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isValidDate(value) ? value : null;
  if (typeof value === 'string' && isYyyyMmDd(value)) {
    const [year, month, day] = value.split('-').map(Number);
    const local = new Date(year, month - 1, day);
    return isValidDate(local) ? local : null;
  }
  const parsed = new Date(value);
  return isValidDate(parsed) ? parsed : null;
};

export function getFormattedDateIso8601(newDate, hourPreset) {
  const date = parseLocalCalendarDate(newDate) || new Date(newDate);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ms = `${String(date.getMilliseconds()).padStart(3, '0')}000`;

  const offsetMinutes = date.getTimezoneOffset();
  const sign = offsetMinutes > 0 ? '-' : '+';
  const absOffset = Math.abs(offsetMinutes);
  const offsetH = String(Math.floor(absOffset / 60)).padStart(2, '0');
  const offsetM = String(absOffset % 60).padStart(2, '0');

  if (hourPreset === 'start') {
    return `${year}-${month}-${day}T00:00:00.000000${sign}${offsetH}:${offsetM}`;
  }
  if (hourPreset === 'end') {
    return `${year}-${month}-${day}T23:59:59.999999${sign}${offsetH}:${offsetM}`;
  }

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}${sign}${offsetH}:${offsetM}`;
}

export const formattedDate = (rawDate, groupedByDay) => {
  const groupDateObject = new Date(`${rawDate}${groupedByDay ? '' : '-01'}T00:00:00-03:00`);
  return datter(groupDateObject).raw.format(groupedByDay ? 'DD/MM/YYYY' : 'MM/YYYY');
};

export const formatDateToHuman = (date, format = 'day') => {
  if (!date || typeof date !== 'string') return '--';

  const [year, month, day] = date.split('-');
  if (!year || !month) return date;

  if (format === 'year') return year;
  if (format === 'month') return `${month}/${year}`;
  return `${day}/${month}/${year}`;
};

export function formattedDateTimezone(dateString, timezone = '03') {
  let date = dateString;
  if (dateString.includes('T')) {
    [date] = dateString.split('T');
  }
  return `${date}T${timezone}:00:00Z`;
}

export const formattedHours = rawSeconds => {
  const time = secondsToDaysHoursMinutes(rawSeconds);
  return [time.H, time.m];
};

export const formattedHoursString = (rawSeconds, showInDays = false) => {
  const time = secondsToDaysHoursMinutes(rawSeconds);
  if (showInDays) {
    const days = time.d > 0 ? `${time.d} dias ` : '';
    return `${days}${time.h}:${time.m}h`;
  }
  return `${time.H}:${time.m}h`;
};

export const defaultParseDate = (newDate, hourPreset) => {
  const parsed = parseLocalCalendarDate(newDate);
  const dateFormatted = parsed ? new Date(parsed.getTime()) : new Date(newDate);
  if (hourPreset === 'start') dateFormatted.setHours(0, 0, 0, 0);
  if (hourPreset === 'end') dateFormatted.setHours(23, 59, 59, 999);
  return dateFormatted;
};

export const parseFilterDateToIso = (newDate, hourPreset) => {
  if (!newDate) return null;
  const dateUpdated = defaultParseDate(newDate, hourPreset);
  return isValidDate(dateUpdated) ? dateUpdated.toISOString() : null;
};

export const resolveDateParam = (dateValue, defaultDate, type, options = {}) => {
  const { useDefaultWhenEmpty = true } = options;
  if (dateValue && dateValue !== '') {
    return isYyyyMmDd(dateValue) ? dateValue : defaultParseDate(dateValue, type)?.toISOString?.();
  }
  return useDefaultWhenEmpty ? defaultParseDate(defaultDate, type)?.toISOString?.() : undefined;
};

export const previousPeriodDate = (newDate, period = 'day', quantity = 1, hourPreset = null) => {
  const parsed = parseLocalCalendarDate(newDate);
  const dateFormatted = parsed ? new Date(parsed.getTime()) : new Date(newDate);
  if (hourPreset === 'start') defaultParseDate(dateFormatted, 'start');
  if (hourPreset === 'end') defaultParseDate(dateFormatted, 'end');
  if (period === 'day') dateFormatted.setDate(dateFormatted.getDate() - quantity);
  if (period === 'week') dateFormatted.setDate(dateFormatted.getDate() - quantity * 7);
  if (period === 'month') dateFormatted.setMonth(dateFormatted.getMonth() - quantity);
  if (period === 'year') dateFormatted.setFullYear(dateFormatted.getFullYear() - quantity);
  return dateFormatted;
};

export const nextPeriodDate = (newDate, period = 'day', quantity = 1, hourPreset = null) => {
  const parsed = parseLocalCalendarDate(newDate);
  const dateFormatted = parsed ? new Date(parsed.getTime()) : new Date(newDate);
  if (hourPreset === 'start') defaultParseDate(dateFormatted, 'start');
  if (hourPreset === 'end') defaultParseDate(dateFormatted, 'end');
  if (period === 'day') dateFormatted.setDate(dateFormatted.getDate() + quantity);
  if (period === 'week') dateFormatted.setDate(dateFormatted.getDate() + quantity * 7);
  if (period === 'month') dateFormatted.setMonth(dateFormatted.getMonth() + quantity);
  if (period === 'year') dateFormatted.setFullYear(dateFormatted.getFullYear() + quantity);
  return dateFormatted;
};

export const phoneFormatter = (value, hideCountryCode = false) => {
  const phone = value.replace(/\D/g, '');
  if (phone.length >= 12 && hideCountryCode) return phone.substring(2);
  return phone;
};

export const formatNumberWithCheckDigit = (number, checkDigit) => {
  if (!number) return '';

  const normalizedNumber = `${number}`.trim().replace(/-+$/, '');
  const normalizedCheckDigit = `${checkDigit || ''}`.trim().replace(/^-+/, '');

  if (!normalizedNumber) return '';
  return normalizedCheckDigit ? `${normalizedNumber}-${normalizedCheckDigit}` : normalizedNumber;
};

export const generateSiteUrl = (site, trailUrl = '') =>
  // eslint-disable-next-line sonarjs/no-nested-template-literals
  `${envs.PROTOCOL}://${site?.domain || `${site?.subdomain}.${envs.WHITELABEL_URL}`}${trailUrl}`;

export const encodeHtmlEntities = text => {
  if (!text) return '';

  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  const decoded = textarea.value;

  return decoded
    .split('')
    .map(char => {
      const code = char.charCodeAt(0);
      if (code > 127) {
        return `&#${code};`;
      }
      return char;
    })
    .join('');
};
